import { useEffect, useState } from 'react';
import type { HelpContent } from './content/types';

/**
 * The help content is its own lazy chunk (`virtual:help-content`, built from docs/help/ by
 * vite.helpContent.ts): fetched the first time help opens, or in the background a few seconds
 * after the console loads, never on the critical path. Images inside it are lazy-loaded <img>s.
 */
let cache: HelpContent | null = null;
let pending: Promise<HelpContent> | null = null;

export function loadHelpContent(): Promise<HelpContent> {
  if (cache) return Promise.resolve(cache);
  pending ??= import('virtual:help-content').then(
    (m) => (cache = m.default),
    (e: unknown) => {
      pending = null;
      throw e;
    },
  );
  return pending;
}

/** Tests: replace (or clear) the loaded content. */
export function setHelpContentForTests(content: HelpContent | null): void {
  cache = content;
  pending = null;
}

export type HelpContentState =
  | { status: 'loading' }
  | { status: 'error'; retry: () => void }
  | { status: 'ready'; content: HelpContent };

export function useHelpContent(enabled = true): HelpContentState {
  const [state, setState] = useState<HelpContentState>(() =>
    cache ? { status: 'ready', content: cache } : { status: 'loading' },
  );
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    if (cache) {
      setState({ status: 'ready', content: cache });
      return;
    }
    let live = true;
    setState({ status: 'loading' });
    loadHelpContent().then(
      (content) => live && setState({ status: 'ready', content }),
      () => live && setState({ status: 'error', retry: () => setAttempt((n) => n + 1) }),
    );
    return () => {
      live = false;
    };
  }, [enabled, attempt]);
  return state;
}
